import { useTranslation } from 'react-i18next';
import dayjs from '@/utils/dayjsConfig';
import { useMemo, useState, useRef, useEffect, useTransition, useCallback } from 'react';
import { Input, Button, Radio, Table, Form, Alert, message, Checkbox } from 'antd';
import clsx from 'clsx';
import { MONTH_FORMAT, MONTH_PARMS, TABLEPAI, YYYY_MM_DD } from '@/enums/pageEnum';
import {
  ProTable,
  ProFormText,
  ProForm,
  ProFormTextArea,
} from '@ant-design/pro-components';
import {
  creatScedule,
  getDescrScedule,
  getLiftAddress,
  getLiftCompany,
  getList,
} from '@api';
import { getDaysInMonth, parseQueryString, parseMonth } from '@/utils/toolt';
import { isEmpty, toString, uniqBy } from 'lodash-es';
import type { ProFormInstance } from '@ant-design/pro-components';
import { CustomModal, FilterComponet } from '@/components';
import { LiftTcLongSuffix } from '@/utils/commonConfig';
import { useMenuItem } from '@/hooks';

export default function LMSchedule() {
  const { t } = useTranslation();
  const currentDate = useMemo(() => new Date(), []);
  const formRef = useRef<ProFormInstance>(null);
  const [titleMonth, setTitleMonth] = useState<Date>(currentDate);
  const [selectedhighRow, setSelectedhighRow] = useState<string | null>();
  const [selectLiftDate, setSelectLiftDate] = useState<any>();
  const [radioValue, setRadioValue] = useState<any>(null);
  const [schedRemarks, setSchedRemarks] = useState<any>({});
  const [isShowDels, setIsShowDels] = useState<boolean>(false);
  const [liftAddress, setLiftAddress] = useState<string>('');
  const [data, setData] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [isPending, startTransition] = useTransition();
  const [liftCompany, setLiftCompany] = useState<any[]>([]);
  const [liftChecks, setLiftChecks] = useState<any>({});
  //上一次勾选的值
  const [pventCheck, setPventCheck] = useState<string | null>(null);

  const getPathParams = useCallback(() => {
    return parseMonth(parseQueryString());
  }, []);

  const getDescription = useCallback(
    async (parms: any) => {
      const res = await getDescrScedule({
        ...parms,
        month: getPathParams().month,
      });
      const remarks = {
        fristRemark: res.data.body[0]?.description,
        fristRemarkId: res.data.body[0]?.id,
        secondRemark: res.data.body[1]?.description,
        secondRemarkId: res.data.body[1]?.id,
      };
      setIsShowDels(false);
      setSchedRemarks(remarks);
      form?.setFieldsValue(remarks);
    },
    [getPathParams, form],
  );

  const getLiftAndAddress = useCallback((parms: any) => {
    if (parms?.liftBlk) {
      getLiftAddress(`${parms.liftTc}-${parms.liftPfx}-${parms.liftBlk}`).then(
        (res: any) => {
          if (res.data?.body && res.data?.body.length) {
            setLiftAddress(res.data.body[0]?.attribute?.address);
          }
        },
      );
    }
    getLiftCompany(parms).then((res: any) => {
      setLiftCompany(
        uniqBy(
          res.data.body.map((item: any) => ({
            value: item.attribute?.maintenanceCompany,
            label: item.attribute?.maintenanceCompany,
          })),
          'value',
        ),
      );
    });
  }, []);

  const clickColKey = useMemo(() => {
    const key = Object.keys(liftChecks).find((item) => liftChecks[item] === false);
    return key;
  }, [liftChecks]);

  const getTableLists = useCallback(
    (prev?: any) => {
      setSchedRemarks({});
      setSelectedhighRow(null);
      setSelectLiftDate(null);
      setRadioValue(null);
      setPventCheck(null);
      setLiftAddress('');
      if (prev && prev?.isReset) {
        setData([]);
        formRef?.current?.resetFields();
        return;
      }
      const parms = getPathParams();
      const keys = Object.keys(parms);
      if (!keys || !(keys.includes('liftTc') && keys.includes('liftPfx'))) {
        setData([]);
        return;
      }
      getLiftAndAddress(parms);
      startTransition(async () => {
        try {
          const res = await getList({
            url: TABLEPAI.LM_SCHEDULE,
            params: {
              ...parms,
              month: parms.month || dayjs(currentDate).format(MONTH_PARMS),
            },
          });
          const temp: { [key: string]: boolean } = {};
          res.data.body.map((item: any) => {
            if (item.schedules && item.schedules?.length) {
              item.schedules.map((li: any) => {
                temp[`${li.liftId}/${li.date}/${li.id}`] = true;
              });
            }
          });
          setLiftChecks(temp);
          setData(res.data.body);
          setTitleMonth(dayjs(formRef?.current?.getFieldValue('month')).toDate());
        } catch (error) {
          message.error(toString(error));
        }
      });
    },
    [getPathParams, currentDate, getLiftAndAddress],
  );

  useMenuItem({ callBack: getTableLists });

  useEffect(() => {
    if (form && !radioValue && !selectedhighRow) {
      form?.resetFields();
    }
  }, [selectedhighRow, radioValue, form]);

  const canOperate = useCallback(
    (liftId: string, newDate: string) => {
      const tempData = data.find((item) => item.liftId === liftId);
      const hasBeenChecks = tempData?.schedules.filter(
        (item: any) => liftChecks[`${item?.liftId}/${item?.date}/${item?.id}`],
      );
      const cuuerMoth = dayjs(newDate).format('YYYYMM');
      if (
        hasBeenChecks.filter((item: any) => item.month.toString() === cuuerMoth)
          .length === 2
      ) {
        return true;
      }
      const restrictedDates = hasBeenChecks
        .map((item: any) => ({
          startDate: new Date(item.date),
          endDate: new Date(item.date),
        }))
        .map((item: any) => {
          item.startDate.setDate(item.startDate.getDate() - 10);
          item.endDate.setDate(item.endDate.getDate() + 10);
          return item;
        });
      const newDateObj = new Date(newDate);
      return restrictedDates.some((range: any) => {
        return newDateObj >= range.startDate && newDateObj <= range.endDate;
      });
    },
    [data, liftChecks],
  );

  const columns: any[] = useMemo(() => {
    const year = titleMonth.getFullYear();
    const month = titleMonth.getMonth();
    const cday = currentDate.getDate();
    const cyear = currentDate.getFullYear();
    const cmonth = currentDate.getMonth();
    const isSelectMonth = cyear === year && cmonth === month;
    const munbers = Array.from(
      {
        length: getDaysInMonth(year, month),
      },
      (_, index) => {
        const itemDate = dayjs(new Date(year, month, index + 1));
        return {
          title: () => {
            const isSunday = itemDate.day() === 0;
            const curDay = cday === index + 1 && isSelectMonth;
            return (
              <div
                className={clsx(
                  'w-9 h-9 flex flex-col items-center justify-center',
                  isSunday && 'bg-[#FF0006] text-white',
                )}
              >
                <p className={clsx('leading-[1px]', curDay && 'mt-[14px]')}>
                  {index + 1}
                  {curDay && (
                    <>
                      <br />
                      <span
                        className={clsx(
                          'w-[6px] h-[6px] mt-[8px] inline-block rounded-full bg-[#FF0006]',
                          isSunday && 'bg-white',
                        )}
                      ></span>
                    </>
                  )}
                </p>
              </div>
            );
          },
          dataIndex: 'liftBlk',
          align: 'center',
          width: 36,
          render: (_: any, record: any) => {
            const dd = itemDate.format(YYYY_MM_DD);
            const selected = record.schedules?.find((item: any) => item.date === dd);
            const keyCheck = `${selected?.liftId}/${selected?.date}/${selected?.id}`;
            const isChecked = selected && liftChecks[keyCheck];
            if (isChecked) {
              return (
                <Checkbox
                  defaultChecked
                  className={clsx(
                    'w-4 !h-4 px-0 py-0',
                    selected.finalization && 'pointerRed bg-[#FF0006]',
                    !selected.originalDate && 'pointerOrange bg-[#FFBF00]',
                  )}
                  onChange={() => {
                    setLiftChecks((pve: any) => {
                      const temp = { ...pve };
                      Object.keys(temp).forEach((key) => {
                        temp[key] = true;
                      });
                      temp[keyCheck] = !pve[keyCheck];
                      return temp;
                    });
                    setPventCheck(keyCheck);
                    setRadioValue(null);
                  }}
                  disabled={selected.finalization}
                />
              );
            }
            const isDisDate = canOperate(record.liftId, dd);
            const clickColKeyId = clickColKey?.split('/')[0];
            return (
              <Radio.Button
                className={clsx(
                  'w-4 !h-4 !px-0 py-0',
                  clickColKeyId === record.liftId && 'modifiedSelected',
                )}
                disabled={itemDate < dayjs(currentDate).add(2, 'day') || isDisDate}
                onClick={() => {
                  setRadioValue(null);
                  if (pventCheck === keyCheck && record.schedules?.length) {
                    setLiftChecks((pve: any) => ({ ...pve, [keyCheck]: true }));
                  }
                  setSelectLiftDate({
                    ...record,
                    date: new Date(year, month, index + 1),
                  });
                }}
                value={`${record.liftId}/${dd}`}
              />
            );
          },
        };
      },
    );
    return [
      { title: t('TC'), dataIndex: 'liftTc', align: 'center' },
      { title: t('Pfx'), dataIndex: 'liftPfx', align: 'center' },
      { title: t('block'), dataIndex: 'liftBlk', align: 'center' },
      { title: t('lift'), dataIndex: 'lift', align: 'center' },
      ...munbers,
    ];
  }, [t, titleMonth, currentDate, clickColKey, liftChecks, pventCheck, canOperate]);

  const isSaved = useMemo(() => {
    const isSav = isEmpty(radioValue);
    if (isSav) {
      setSelectLiftDate(null);
    }
    if (isShowDels && selectedhighRow) return false;
    return isSav || isShowDels;
  }, [radioValue, isShowDels, selectedhighRow]);

  const getchangeDateReason = useMemo(() => {
    if (clickColKey && selectLiftDate) {
      const renaon = selectLiftDate?.schedules?.find(
        (item: any) => `${item.liftId}/${item.date}/${item.id}` === clickColKey,
      );
      return { changeDateReason: renaon?.changeDateReason };
    }
    return {};
  }, [clickColKey, selectLiftDate]);

  return (
    <div className="p-4">
      <div>
        <Alert
          message="Lift must be entered to search!"
          showIcon
          type="warning"
          closable
        />
      </div>
      <FilterComponet
        fieldsProp={[
          {
            ...LiftTcLongSuffix(t),
            fieldProps: {
              onChange: () => {
                const parms = formRef?.current?.getFieldsValue();
                setLiftAddress('');
                if (parms.liftTc && parms.liftPfx && parms.liftBlk) {
                  getLiftAndAddress(parms);
                  return;
                }
                setLiftCompany([]);
              },
            },
          },
          {
            field: 'liftGroup',
            label: t('liftGroup'),
            component: 'select',
            options: [],
            fieldProps: { style: { width: '200px' } },
          },
          {
            field: 'liftCompany',
            label: t('LIFT_COMPANY'),
            component: 'select',
            options: liftCompany,
            fieldProps: { style: { width: '360px' } },
          },
          {
            field: 'month',
            label: t('scheduleFo'),
            component: 'monthDate',
            fieldProps: {
              maxDate: dayjs(
                new Date(currentDate).setFullYear(currentDate.getFullYear() + 1),
              ),
              format: MONTH_FORMAT,
              style: { width: '200px' },
              allowClear: false,
            },
          },
        ]}
        ref={formRef}
        submit={(val) => {
          const keys = Object.keys(val);
          if (!keys || !(keys.includes('liftTc') && keys.includes('liftPfx'))) {
            message.warning('Lift cannot be empty!');
          }
          setTitleMonth(dayjs(formRef?.current?.getFieldValue('month')).toDate());
          getTableLists();
          form?.resetFields();
        }}
        reset={() => {
          getTableLists();
          setTitleMonth(dayjs(formRef?.current?.getFieldValue('month')).toDate());
        }}
      />
      <div className="bg-[#fff] dark:bg-[#141414] pb-5 mb-[2px]">
        <span className="inline-block w-[118px] pl-4">{t('address')}: </span>
        <Input value={liftAddress} readOnly className="w-[360px]" />
      </div>
      <ProForm
        layout="horizontal"
        form={form}
        submitter={false}
        labelAlign="left"
        labelCol={{ span: 3 }}
        onValuesChange={(_, values) => {
          if (
            values.fristRemark !== schedRemarks?.fristRemark ||
            values.secondRemark !== schedRemarks?.secondRemark
          ) {
            setIsShowDels(true);
          } else {
            setIsShowDels(false);
          }
        }}
      >
        <div className="bg-[#fff] dark:bg-[#141414] mb-[2px] p-4">
          <div>
            <ProFormText
              name="fristRemark"
              label="1st schedule date:"
              disabled={!selectedhighRow}
              fieldProps={{ style: { width: '65%' } }}
              placeholder="Enter schedule 1 remarks"
            />
            <ProFormText
              name="secondRemark"
              label="2nd schedule date:"
              disabled={!selectedhighRow}
              fieldProps={{ style: { width: '65%' } }}
              placeholder="Enter schedule 2 remarks"
            />
          </div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center justify-start gap-7 leading-5">
              {[
                { name: 'newSchedule', color: 'bg-[#0364FF]' },
                { name: 'modifiedSched', color: 'bg-[#FFBF00]' },
                { name: 'durationCalcul', color: 'bg-[#FF0006]' },
                { name: 'disabled', color: 'bg-[#A9A9A5]' },
              ].map(({ name, color }) => (
                <div key={name}>
                  <span
                    className={`inline-block w-[18px] h-[18px] mr-2 float-left ${color}`}
                  ></span>
                  {t(name)}
                </div>
              ))}
            </div>
            <div>
              <CustomModal
                title={t('save')}
                modalName="Lift maintenance confirmation"
                isProForm={!!clickColKey && selectLiftDate}
                onClick={async (vals, callBack) => {
                  try {
                    const values = form?.getFieldsValue();
                    // 判断是否改变
                    const parms: any = {
                      lmScheduleDescriptionDto: isShowDels
                        ? Object.keys(values).map((key) => ({
                            month: dayjs(titleMonth).format(MONTH_PARMS),
                            liftId: selectedhighRow,
                            description: values[key] || '',
                            id: schedRemarks[`${key}Id`] || null,
                          }))
                        : [],
                    };
                    if (selectLiftDate) {
                      parms.lmScheduleDto = {
                        id: clickColKey ? clickColKey?.split('/')[2] : null,
                        liftId: selectedhighRow,
                        date: selectLiftDate?.date
                          ? dayjs(selectLiftDate?.date).format(YYYY_MM_DD)
                          : '',
                        ...vals,
                      };
                    }
                    await creatScedule(parms);
                    message.success(t('success'));
                    callBack?.(false);
                    getTableLists();
                  } catch (error) {
                    message.error(JSON.stringify(error));
                    callBack?.(true);
                  }
                  form?.submit();
                }}
                layout="vertical"
                initValues={getchangeDateReason}
                buttonCogfig={{ disabled: isSaved }}
              >
                {clickColKey && selectLiftDate ? (
                  <ProFormTextArea
                    name="changeDateReason"
                    label={t('rescheduling')}
                    rules={[{ required: true, message: 'Please enter reason' }]}
                    fieldProps={{ placeholder: 'Time conflict...' }}
                  />
                ) : (
                  <p className="pb-7 pt-4 pl-2">{t('hint.areMainSched')}</p>
                )}
              </CustomModal>
              <Button color="primary" variant="outlined" className="ml-3">
                {t('upload')}
              </Button>
            </div>
          </div>
        </div>
      </ProForm>
      <div className="bg-[#fff] dark:bg-[#141414] mb-[2px] min-h-[calc(100vh-488px)] p-4 pb-3">
        <Table
          columns={[{ title: 'Name', dataIndex: 'name', key: 'name' }]}
          scroll={{ x: 'max-content' }}
          showHeader={false}
          rowKey={'id'}
          className="scheduleTableExpand"
          dataSource={[{ id: 'expand' }]}
          pagination={false}
          expandable={{
            expandedRowRender: () => (
              <Radio.Group
                buttonStyle="solid"
                className="w-full overflow-hidden scheduRadio"
                value={radioValue}
                onChange={(e) => {
                  if (!pventCheck?.includes(e.target.value)) {
                    setRadioValue(e.target.value);
                  }
                }}
              >
                <ProTable
                  search={false}
                  columns={columns}
                  options={false}
                  bordered
                  loading={isPending}
                  rowKey={'liftId'}
                  rowHoverable
                  scroll={{
                    x: 'max-content',
                  }}
                  onRow={(record) => ({
                    onClick: () => {
                      setSelectedhighRow(record.liftId);
                      if (selectedhighRow && selectedhighRow === record.liftId) {
                        return;
                      }
                      setRadioValue(null);
                      const currentLiftid = pventCheck?.split('/')[0];
                      setLiftChecks((pve: any) => {
                        const temp = { ...pve };
                        Object.keys(temp).forEach((key) => {
                          temp[key] = true;
                        });
                        if (record.liftId !== currentLiftid) {
                          record.schedules?.map((item: any) => {
                            const key = `${item?.liftId}/${item?.date}/${item?.id}`;
                            temp[key] = pventCheck === key ? !pve[key] : true;
                          });
                        }
                        return temp;
                      });
                      getDescription({ liftId: record.liftId });
                    },
                  })}
                  dataSource={data}
                  rowClassName={(record) => {
                    return selectLiftDate?.liftId === record?.liftId ||
                      selectedhighRow === record?.liftId ||
                      radioValue === record?.liftId
                      ? 'highlightRow'
                      : '';
                  }}
                  columnEmptyText=" "
                  className="border-[#F1F0F5] dark:border-[#ffffff21] border-[1px] lmScheduleClass"
                  pagination={false}
                  rowSelection={false}
                />
              </Radio.Group>
            ),
            defaultExpandedRowKeys: ['expand'],
          }}
        />
      </div>
    </div>
  );
}
